import {RootLayout, RsnIconWidget, Skeleton, styles, usePrevious} from '@raison/ui-kit';
import React, {ElementRef, Fragment, PropsWithChildren, useMemo, useRef, useState} from 'react';
import {Transition, TransitionGroup, TransitionStatus} from 'react-transition-group';
import ListLayout from '~/shared/layouts/ListLayout/ListLayout';
import {CurrencyOptionBase} from './interfaces';

export interface LayoutProps {
    title: string;
    verb: string;
}

export interface ScreenProps extends LayoutProps {
    operations: CurrencyOptionProps[];
}

export interface CurrencyOptionProps extends CurrencyOptionBase {
    onClick: () => void;
}

export function Layout({title, verb, children}: PropsWithChildren<LayoutProps>) {
    return (
        <RootLayout title={title}>
            <div className='mb-2 px-4 pt-6'>
                <h1 className='fv-heading-l text-neutral-100'>Currency selection</h1>
                <p className='fv-text-l-regular mt-2 text-neutral-90'>
                    Please select the currency that you would like to {verb}
                </p>
            </div>
            {children}
        </RootLayout>
    );
}

export function Option({onClick, icon, name, shortName}: CurrencyOptionProps): React.JSX.Element {
    return (
        <RsnIconWidget.Lg
            className='py-4'
            onClick={onClick}
            icon={{src: icon, size: 'lg'}}
            addon={<ListLayout.Arrow />}
        >
            <RsnIconWidget.TextBlock heading={name} details={shortName} />
        </RsnIconWidget.Lg>
    );
}

export function LoadingScreen(props: LayoutProps) {
    return (
        <Layout {...props}>
            <ListLayout>
                {Array.from({length: 6}).map((_, i) => (
                    <RsnIconWidget.Lg
                        key={i}
                        className='py-4'
                        icon={{element: <Skeleton circle width='100%' height='100%' />}}
                        addon={<ListLayout.Arrow />}
                    >
                        <RsnIconWidget.TextBlock heading={<Skeleton width={60} />} details={<Skeleton width={120} />} />
                    </RsnIconWidget.Lg>
                ))}
            </ListLayout>
        </Layout>
    );
}

export const For = <T,>({
    each,
    empty,
    children,
}: {
    each: T[] | undefined | null;
    children: (item: T) => React.JSX.Element;
    empty?: React.JSX.Element;
}) => <Fragment>{!each || each.length === 0 ? empty : each?.map(item => children(item))}</Fragment>;

type TabObject = {
    label: string;
    element: React.JSX.Element;
};

export const SimpleTabs = ({tabs, className}: {className?: string; tabs: TabObject[]}) => {
    const [activeTab, setActiveTab] = useState(0);
    const previousTab = usePrevious(activeTab) ?? activeTab;
    const direction = useRef(-1);
    direction.current = Math.sign(previousTab - activeTab);

    const refs = useMemo(() => tabs.map(() => ({current: null as ElementRef<'div'> | null})), [tabs.length]);
    const nodeRef = refs[activeTab];

    const duration = 150;

    const baseStyles: React.CSSProperties = {
        position: 'absolute',
        width: '100%',
        transitionProperty: 'transform, opacity',
        transitionDuration: `${duration}ms`,
        transitionTimingFunction: 'ease-in-out',
        transform: 'translateX(0)',
    };

    const statusStyles = (): {[K in TransitionStatus]?: React.CSSProperties} => ({
        exited: {transform: `translateX(${-40 * direction.current}%)`, opacity: 0},
        entering: {transform: 'translateX(0)', transitionDelay: `${duration / 2}ms`},
        entered: {transform: 'translateX(0)'},
        exiting: {transform: `translateX(${40 * direction.current}%)`, opacity: 0},
    });

    const getTabStyle = (status: TransitionStatus): React.CSSProperties => ({
        ...baseStyles,
        ...statusStyles()[status],
    });

    return (
        <div className={className}>
            <div className={cls.tabPanel}>
                {tabs.map((tab, i) => (
                    <button
                        key={tab.label}
                        className={`${cls.tabLabel} ${activeTab === i ? cls.tabLabelActive : cls.tabLabelInactive}`}
                        onClick={() => setActiveTab(i)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <TransitionGroup component='div' style={{position: 'relative', width: '100%'}}>
                <Transition key={activeTab} nodeRef={nodeRef} timeout={duration} mountOnEnter unmountOnExit>
                    {status => (
                        <div key={activeTab} ref={nodeRef} style={getTabStyle(status)}>
                            {tabs[activeTab].element}
                        </div>
                    )}
                </Transition>
            </TransitionGroup>
        </div>
    );
};

export const renderEmptyTab = () => <div className={cls.emptyTab}>No currencies available for this operation yet</div>;

const cls = styles({
    tabPanel: 'padding flex gap-2 p-4',
    tabLabel: 'fv-text-m-bold flex h-8 flex-1 items-center justify-center rounded-sm border border-solid',
    tabLabelActive: 'border-neutral-100 bg-neutral-100 text-neutral-10',
    tabLabelInactive: 'border-neutral-30 bg-neutral-20 text-neutral-100',
    emptyTab: 'fv-text-xl-regular py-4 text-center text-neutral-60',
});
